/**
 * t480u: the desktop sidebar's "Find a channel or person" box (beside the Chat heading). Typing filters the sidebar
 * itself (every section, the channels without a section, ad-hoc meeting rooms and your DMs), then adds people you could
 * message and public channels you could join. Pure helpers here; Sidebar.tsx renders them.
 */
import type { Conversation, DirectoryPerson, WorkspaceMember } from '@tradeoutlet/shared';
import type { SectionNode } from '@/lib/sectionTree';

export const SIDEBAR_FILTER_PLACEHOLDER = 'Find a channel or person';
export const SIDEBAR_FILTER_LABEL = 'Find a channel or person';
export const NO_MATCHES = 'No matches';
export const PEOPLE_HEADING = 'People';
export const OTHER_CHANNELS_HEADING = 'Other channels';

export const normFilter = (q: string) => q.trim().toLowerCase().replace(/^#/, '');

/** Case-insensitive "contains" on any of the given texts; an empty query matches everything. */
export function textMatches(q: string, ...texts: (string | null | undefined)[]): boolean {
  const t = normFilter(q);
  if (!t) return true;
  return texts.some(s => (s ?? '').toLowerCase().includes(t));
}

/** A word in any of the texts starts with the query ("ware" finds "Warehouse Supervisor"; "tom" doesn't find "Customer"). */
export function wordStarts(q: string, ...texts: (string | null | undefined)[]): boolean {
  const t = normFilter(q);
  if (!t) return true;
  return texts.some(s => {
    const low = (s ?? '').toLowerCase();
    return low.startsWith(t) || low.split(/[\s\-_/,&()·]+/).some(w => w.startsWith(t)) || low.includes(` ${t}`);
  });
}

/** Conversations whose shown name matches (channels by name, DMs by the other people's names). */
export function filterConversations(list: Conversation[], q: string, name: (c: Conversation) => string): Conversation[] {
  if (!normFilter(q)) return list;
  return list.filter(c => textMatches(q, name(c), c.slug, c.title));
}

/** Drops sections with no channels anywhere beneath them (while filtering, empty sections are hidden). */
export function pruneSections(nodes: SectionNode[]): SectionNode[] {
  return nodes.flatMap(n => {
    const children = pruneSections(n.children);
    return children.length || n.channels.length ? [{ ...n, children }] : [];
  });
}

export interface PersonResult {
  member: WorkspaceMember;
  /** Your 1:1 DM with them, if you have one. */
  dmId: string | null;
  /** Job title, or their building when there's no title. */
  detail: string | null;
}

/**
 * People you can message who match by name (anywhere in it), or by a word of their job title or building. Same rule as starting a DM (the API's
 * assertWorkspaceMembers): active members of the workspace, never you. People whose 1:1 DM is already listed in the
 * filtered Direct messages (`shownDmWith`) are left out so nobody shows twice. Name matches come first.
 */
export function matchPeople(members: WorkspaceMember[], directory: DirectoryPerson[], conversations: Conversation[], me: string | null, q: string,
  shownDmWith: Set<string> = new Set(), limit = 8): PersonResult[] {
  const t = normFilter(q);
  if (!t) return [];
  const building = new Map(directory.map(p => [p.user_id, p.building]));
  const dmWith = new Map<string, string>();
  for (const c of conversations) {
    if (c.kind !== 'dm') continue;
    const other = c.members?.find(m => m.user_id !== me);
    if (other) dmWith.set(other.user_id, c.id);
  }
  const rank = (m: WorkspaceMember) => {
    const n = m.display_name.toLowerCase();
    return n.startsWith(t) || n.includes(` ${t}`) ? 0 : n.includes(t) ? 1 : 2;
  };
  return members
    .filter(m => m.user_id !== me && m.status === 'active' && !shownDmWith.has(m.user_id)
      && (textMatches(t, m.display_name) || wordStarts(t, m.job_title, building.get(m.user_id))))
    .sort((a, b) => rank(a) - rank(b) || a.display_name.localeCompare(b.display_name))
    .slice(0, limit)
    .map(member => ({ member, dmId: dmWith.get(member.user_id) ?? null, detail: member.job_title || building.get(member.user_id) || null }));
}

/**
 * Public channels you're not in that match (what the old Channels-heading search offered with "Join").
 * `browse` is the workspace channel directory; private channels you're not in never show; closed ones never show.
 */
export function otherChannels(browse: Conversation[], mine: Conversation[], q: string, limit = 8): Conversation[] {
  const t = normFilter(q);
  if (!t) return [];
  const joined = new Set(mine.map(c => c.id));
  return browse
    .filter(c => c.kind === 'channel' && !c.archived_at && !c.is_private && !c.is_member && !joined.has(c.id) && textMatches(t, c.slug, c.title))
    .sort((a, b) => Number(!(a.slug ?? '').toLowerCase().startsWith(t)) - Number(!(b.slug ?? '').toLowerCase().startsWith(t)) || (a.slug ?? '').localeCompare(b.slug ?? ''))
    .slice(0, limit);
}

/** Next highlighted index for Up/Down, staying within the results. */
export function moveHighlight(current: number, count: number, key: 'ArrowUp' | 'ArrowDown'): number {
  if (count <= 0) return 0;
  if (key === 'ArrowDown') return Math.min(current + 1, count - 1);
  return Math.max(current - 1, 0);
}
